import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Minus, Plus, X } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/api";

const SHIPPING_EST = 5.0;

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, discount, discountCode, setDiscountCode } = useCart();
  const [codeInput, setCodeInput] = useState(discountCode);
  const navigate = useNavigate();

  const estTax = (subtotal - discount) * 0.08;
  const total = subtotal - discount + SHIPPING_EST + estTax;

  const applyCode = (e) => {
    e.preventDefault();
    if (codeInput.trim().toUpperCase() === "DR10") {
      setDiscountCode("DR10");
      toast.success("DR10 APPLIED — 10% OFF");
    } else {
      toast.error("CODE NOT RECOGNIZED");
    }
  };

  return (
    <div data-testid="cart-page">
      <PageHeader overline="YOUR SELECTION" title="Cart" />
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 py-14">
        {items.length === 0 ? (
          <div className="border border-wire px-8 py-24 text-center" data-testid="cart-page-empty">
            <p className="font-display text-2xl font-extrabold uppercase tracking-tight text-neutral-500">Your cart is empty</p>
            <p className="mt-4 font-mono text-xs tracking-[0.25em] text-neutral-600">THE OUTFIT IS WAITING.</p>
            <Link to="/shop" data-testid="cart-page-shop-link"
              className="mt-8 inline-block bg-white px-10 py-4 font-mono text-[11px] tracking-[0.35em] text-black transition-colors hover:bg-neutral-300">
              SHOP NOW
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="border-t border-wire">
                {items.map((item) => (
                  <div key={`${item.slug}-${item.size}`} data-testid={`cart-page-item-${item.size}`} className="flex gap-5 border-b border-wire py-6">
                    <Link to={`/product/${item.slug}`} className="h-28 w-24 shrink-0 overflow-hidden border border-wire bg-charcoal">
                      <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
                    </Link>
                    <div className="flex flex-1 flex-col">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <Link to={`/product/${item.slug}`} className="font-display text-base font-bold uppercase tracking-wide text-white hover:underline underline-offset-4">
                            {item.name}
                          </Link>
                          <p className="mt-1 font-mono text-[10px] tracking-[0.25em] text-neutral-500">SIZE {item.size} — UNISEX</p>
                        </div>
                        <button data-testid={`cart-page-remove-${item.size}`} onClick={() => removeItem(item.slug, item.size)}
                          className="p-1 text-neutral-600 transition-colors hover:text-white" aria-label="Remove item">
                          <X size={16} />
                        </button>
                      </div>
                      <div className="mt-auto flex items-center justify-between pt-4">
                        <div className="flex items-center border border-wire">
                          <button data-testid={`cart-page-minus-${item.size}`} onClick={() => updateQty(item.slug, item.size, item.qty - 1)} className="px-3 py-2 text-neutral-400 hover:text-white" aria-label="Decrease quantity">
                            <Minus size={12} />
                          </button>
                          <span data-testid={`cart-page-qty-${item.size}`} className="px-4 font-mono text-xs text-white">{item.qty}</span>
                          <button data-testid={`cart-page-plus-${item.size}`} onClick={() => updateQty(item.slug, item.size, item.qty + 1)} className="px-3 py-2 text-neutral-400 hover:text-white" aria-label="Increase quantity">
                            <Plus size={12} />
                          </button>
                        </div>
                        <p className="font-mono text-sm text-neutral-100">{money(item.price * item.qty)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="border border-wire bg-charcoal/50 p-8" data-testid="cart-summary">
                <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">ORDER SUMMARY</p>

                <form onSubmit={applyCode} className="mt-6 flex gap-2">
                  <input
                    data-testid="discount-code-input"
                    value={codeInput}
                    onChange={(e) => setCodeInput(e.target.value)}
                    placeholder="DISCOUNT CODE"
                    className="flex-1 border border-wire bg-transparent px-4 py-3 font-mono text-[11px] tracking-[0.2em] text-white placeholder:text-neutral-600 focus:border-white focus:outline-none"
                  />
                  <button data-testid="discount-apply-button" className="border border-wire px-5 font-mono text-[10px] tracking-[0.3em] text-neutral-300 transition-colors hover:border-white hover:text-white">
                    APPLY
                  </button>
                </form>
                {discount > 0 && (
                  <p className="mt-2 font-mono text-[10px] tracking-[0.2em] text-neutral-400" data-testid="discount-applied-note">DR10 — 10% OFF APPLIED</p>
                )}

                <div className="mt-8 space-y-3 font-mono text-xs tracking-[0.2em]">
                  <div className="flex justify-between text-neutral-400">
                    <span>SUBTOTAL</span><span data-testid="cart-subtotal" className="text-neutral-100">{money(subtotal)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-neutral-400">
                      <span>DISCOUNT</span><span data-testid="cart-discount" className="text-neutral-100">-{money(discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-neutral-400">
                    <span>SHIPPING (STANDARD)</span><span data-testid="cart-shipping" className="text-neutral-100">{money(SHIPPING_EST)}</span>
                  </div>
                  <div className="flex justify-between text-neutral-400">
                    <span>ESTIMATED TAX</span><span data-testid="cart-tax" className="text-neutral-100">{money(estTax)}</span>
                  </div>
                  <div className="flex justify-between border-t border-wire pt-4 text-sm text-white">
                    <span>ESTIMATED TOTAL</span><span data-testid="cart-total">{money(total)}</span>
                  </div>
                </div>

                <button
                  data-testid="cart-checkout-button"
                  onClick={() => navigate("/checkout")}
                  className="group mt-8 flex w-full items-center justify-center gap-3 bg-white py-4 font-mono text-[11px] tracking-[0.35em] text-black transition-colors hover:bg-neutral-300"
                >
                  CHECKOUT <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </button>
                <p className="mt-4 text-center font-mono text-[9px] tracking-[0.2em] text-neutral-600">
                  FINAL SHIPPING & TAX CONFIRMED AT SECURE CHECKOUT
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
